import { defineStore } from 'pinia';
import { ref } from 'vue';
import { ManageOrganisationService } from '@/services/manageOrganisation.services';
import type {
  IManageOrganisation,
  ICreateOrganisationBody,
  IUpdateOrganisationBody,
  IApiResponseManageOrganisation,
  IApiResponseOrganisationWithDetails,
} from '@/types/manageOrgCommerce.types';

/**
 * Store Pinia pour la gestion des organisations.
 */
export const useManageOrganisationStore = defineStore(
  'manageOrganisation',
  () => {
    const organisations = ref<IManageOrganisation[]>([]);
    const currentOrganisation = ref<IManageOrganisation | null>(null);
    const currentOrganisationFull =
      ref<IApiResponseOrganisationWithDetails | null>(null);
    const page = ref(1);
    const total = ref(0);
    const pageSize = ref(20);
    const status = ref<string[]>([]);
    const sortBy = ref('');
    const sortOrder = ref('');

    /**
     * Crée une nouvelle organisation.
     * @param data - Corps de la requête
     */
    const createOrganisation = async (
      data: ICreateOrganisationBody,
    ): Promise<IManageOrganisation> => {
      try {
        const response =
          await ManageOrganisationService.createOrganisation(data);
        // console.log("res",response)
        return response as unknown as IManageOrganisation;
      } catch (error) {
        return Promise.reject(error);
      }
    };

    /**
     * Récupère la liste paginée des organisations.
     */
    const searchOrganisations = async (): Promise<IManageOrganisation[]> => {
      try {
        const response = await ManageOrganisationService.searchOrganisations(
          page.value,
          status.value,
          sortBy.value,
          sortOrder.value,
        );

        const list: IManageOrganisation[] = Array.isArray(response.data)
          ? (response.data as unknown as IManageOrganisation[])
          : ((response.data as any)?.data ?? []);
        organisations.value = list;
        total.value = (response.data as any)?.total ?? list.length;
        pageSize.value = (response.data as any)?.pageSize ?? 20;
        return list;
      } catch (error) {
        return Promise.reject(error);
      }
    };

    /**
     * Récupère une organisation par son ID.
     * @param organizationId - UUID de l'organisation
     */
    const getOrganisationById = async (
      organizationId: string,
    ): Promise<IManageOrganisation> => {
      try {
        const response =
          await ManageOrganisationService.getOrganisationById(organizationId);
        currentOrganisation.value = response.data;
        return response.data;
      } catch (error) {
        return Promise.reject(error);
      }
    };

    /**
     * Récupère une organisation avec ses adresses et contacts.
     * @param organizationId - UUID de l'organisation
     */
    const getOrganisationContactAdress = async (
      organizationId: string,
    ): Promise<IApiResponseOrganisationWithDetails> => {
      try {
        const response =
          await ManageOrganisationService.getOrganisationContactAdress(
            organizationId,
          );
        currentOrganisationFull.value = response.data;
        return response.data;
      } catch (error) {
        return Promise.reject(error);
      }
    };

    /**
     * Récupère une organisation par son slug.
     * @param slug - Slug de l'organisation
     */
    const getOrganisationBySlug = async (
      slug: string,
    ): Promise<IManageOrganisation> => {
      try {
        const response =
          await ManageOrganisationService.getOrganisationBySlug(slug);
        currentOrganisation.value = response.data;
        return response.data;
      } catch (error) {
        return Promise.reject(error);
      }
    };

    /**
     * Met à jour une organisation existante.
     * @param organizationId - UUID de l'organisation
     * @param data - Corps de la requête
     */
    const updateOrganisation = async (
      organizationId: string,
      data: IUpdateOrganisationBody,
    ): Promise<IManageOrganisation> => {
      try {
        const response = await ManageOrganisationService.updateOrganisation(
          organizationId,
          data,
        );
        currentOrganisation.value = response.data.data;
        return response.data.data;
      } catch (error) {
        return Promise.reject(error);
      }
    };

    /**
     * Désactive une organisation.
     * @param organizationId - UUID de l'organisation
     */
    const deactivateOrganisation = async (
      organizationId: string,
    ): Promise<void> => {
      try {
        await ManageOrganisationService.deactivateOrganisation(organizationId);
      } catch (error) {
        return Promise.reject(error);
      }
    };

    /**
     * Associe un logo (déjà uploadé) à une organisation via son ID.
     * @param organizationId - UUID de l'organisation
     * @param logoId - Identifiant du fichier logo, ou null pour le supprimer
     */
    const setOrganisationLogoId = async (
      organizationId: string,
      logoId: string | null,
    ): Promise<IManageOrganisation> => {
      try {
        const response = await ManageOrganisationService.setOrganisationLogoId(
          organizationId,
          logoId,
        );
        return response.data.data;
      } catch (error) {
        return Promise.reject(error);
      }
    };

    /**
     * Récupère une organisation par son numéro d'identification fiscale (TVA).
     * @param taxIdentifier - Numéro TVA de l'organisation
     */
    const getOrganisationByTaxId = async (
      taxIdentifier: string,
    ): Promise<IManageOrganisation> => {
      try {
        const response =
          await ManageOrganisationService.getOrganisationByTaxId(taxIdentifier);
        currentOrganisation.value = response.data;
        return response.data;
      } catch (error) {
        return Promise.reject(error);
      }
    };

    /**
     * Met à jour le logo d'une organisation.
     * @param organizationId - UUID de l'organisation
     * @param file - Fichier image
     */
    const updateOrganisationLogo = async (
      organizationId: string,
      file: File,
    ): Promise<IManageOrganisation> => {
      try {
        const response = await ManageOrganisationService.updateOrganisationLogo(
          organizationId,
          file,
        );
        return response.data.data;
      } catch (error) {
        return Promise.reject(error);
      }
    };

    return {
      organisations,
      currentOrganisation,
      currentOrganisationFull,
      page,
      total,
      pageSize,
      status,
      sortBy,
      sortOrder,
      createOrganisation,
      searchOrganisations,
      getOrganisationById,
      getOrganisationContactAdress,
      getOrganisationBySlug,
      getOrganisationByTaxId,
      setOrganisationLogoId,
      updateOrganisation,
      deactivateOrganisation,
      updateOrganisationLogo,
    };
  },
);
